











































import Vue from 'vue';
import { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';
import { mapGetters } from 'vuex';
import { Lead } from '@/requests/mainApi/models/Lead';
import { getAccessToken } from '@/utils/plugins/jwt';
import { BASE_PATH } from '@/requests/requestMachine';
import UiModal from '@/components/ui/UiModal.vue';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'pdf', 'xls', 'xlsx', 'doc', 'docx'];
const MAX_FILE_SIZE = 30 * 1024 * 1024;

interface Data {
  files: File[];
  error: string;
  isDragOver: boolean;
  submitting: boolean;
  submitted: boolean;
}

interface Methods {
  openFileDialog: () => void;
  onInputChange: (e: Event) => void;
  onDrop: (e: DragEvent) => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  addFiles: (fileList: FileList | null) => void;
  removeFile: (index: number) => void;
  submit: () => void;
}

interface Computed {
  acceptAttr: string;
  currentLeadForDocuments: Lead | null;
  leadId: number | null;
}

interface Props {
  close: () => void;
}

export default Vue.extend({
  name: 'SubmitDocumentsModal',
  components: {
    UiModal,
  },
  props: {
    close: null,
  },
  data() {
    return {
      files: [],
      error: '',
      isDragOver: false,
      submitting: false,
      submitted: false,
    };
  },
  computed: {
    ...mapGetters(['currentLeadForDocuments']),
    acceptAttr(): string {
      return ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',');
    },
    leadId(): number | null {
      return this.currentLeadForDocuments ? this.currentLeadForDocuments.leadId ?? null : null;
    },
  },
  mounted() {
    (this.$refs.modal as any).open();
  },
  // Модалка закрывается через размонтирование (v-if в AppTemplate), поэтому watcher
  // UiModal не успевает вернуть скролл странице - снимаем блокировку body вручную.
  beforeDestroy() {
    document.body.style.overflow = 'auto';
  },
  methods: {
    openFileDialog() {
      (this.$refs.fileInput as HTMLInputElement).click();
    },
    onInputChange(e: Event) {
      const input = e.target as HTMLInputElement;
      this.addFiles(input.files);
      input.value = '';
    },
    onDrop(e: DragEvent) {
      this.isDragOver = false;
      this.addFiles(e.dataTransfer ? e.dataTransfer.files : null);
    },
    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    addFiles(fileList: FileList | null) {
      if (!fileList) return;

      this.error = '';

      Array.from(fileList).forEach(file => {
        const extension = (file.name.split('.').pop() || '').toLowerCase();

        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          this.error = this.$t('app.profile.flatsList.dealsTable.submitDocumentsModal.formatError') as string;
          return;
        }

        if (file.size > MAX_FILE_SIZE) {
          this.error = this.$t('app.profile.flatsList.dealsTable.submitDocumentsModal.sizeError') as string;
          return;
        }

        this.files.push(file);
      });
    },
    removeFile(index: number) {
      this.files.splice(index, 1);
    },
    submit() {
      if (!this.files.length) {
        this.error = this.$t('app.profile.flatsList.dealsTable.submitDocumentsModal.emptyError') as string;
        return;
      }

      this.submitting = true;
      this.error = '';

      const formData = new FormData();
      formData.append('lead_id', String(this.leadId));
      this.files.forEach(file => formData.append('documents[]', file));

      fetch(`${BASE_PATH}/client/v1/upload-documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: formData,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('upload failed');
          }
          this.submitted = true;
        })
        .catch(() => {
          this.error = this.$t('app.profile.flatsList.dealsTable.submitDocumentsModal.submitError') as string;
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
} as ThisTypedComponentOptionsWithRecordProps<Vue, Data, Methods, Computed, Props>);
